import { useMemo, useEffect, useCallback } from 'react';

import { useSetAtom, useAtomValue } from 'jotai';

import { useTranslate } from '@i18n';
import { useUpdateLayout, useUpdateParameters } from '@queries';
import {
    layoutFloorGraphLinesAtom,
    uiNodesMapAtom,
    activeUserIdAtom,
    layoutAtom,
    layoutFloorModulesAtom,
    layoutKeyParameterAtom,
    layoutKioskModulesAtom,
} from '@store';

import { graphLinesMapAtom, gridModulesAtom } from '../../GridAreaControls/atoms';
import { LayoutModule } from '../../GridAreaControls/types';

/**
 * Sync initial modules to GridArea internal values.
 * 
 * See: 
 * ``` 
 *  setLayoutModules = useSetAtom(gridModulesAtom);
 *  ...
 * 
 *  useEffect(() => {
        setLayoutModules(mappedFloorModules);
    }, [mappedFloorModules, setLayoutModules]);
 * ```
 */
export const useSyncFloorModules = () => {
    const floorModules = useAtomValue(layoutFloorModulesAtom);
    const setLayoutModules = useSetAtom(gridModulesAtom);

    const uiNodesMap = useAtomValue(uiNodesMapAtom);

    const t = useTranslate();

    const mappedFloorModules = useMemo(
        () =>
            floorModules
                .sort(({ childDisplayOrder: a }, { childDisplayOrder: b }) => Number(a) - Number(b))
                .reduce((acc, { childUiNodeId, childMetadata }) => {
                    // TODO: internal data structure for grid modules is not ideal
                    // We will want to revisit it and refactor to be easier to map between layout and back.
                    const {
                        colSpan = 1,
                        rowSpan = 1,
                        index,
                    } = childMetadata as Partial<Record<'colSpan' | 'rowSpan' | 'index', number>>;

                    let startIndex = 0;
                    if (typeof index === 'number') {
                        startIndex = index;
                    } else {
                        const prev = acc.at(-1);
                        if (prev) startIndex = prev.index + prev.area.length;
                    }

                    if (startIndex >= 4) {
                        return acc;
                    }

                    const startRow = Math.floor(startIndex / 2);
                    const startCol = startIndex % 2;

                    const area = Array.from({ length: rowSpan * colSpan }).reduce<number[]>((cells, _, i) => {
                        const rowOffset = Math.floor(i / colSpan);
                        const colOffset = i % colSpan;
                        cells.push((startRow + rowOffset) * 2 + (startCol + colOffset));
                        return cells;
                    }, []);

                    const module = uiNodesMap[childUiNodeId];

                    acc.push({
                        area,
                        index: area[0],
                        moduleId: childUiNodeId,
                        x: colSpan,
                        y: rowSpan,
                        moduleName: t(module?.i18n ?? ''),
                    });

                    return acc;
                }, [] as LayoutModule[]),
        [floorModules, t, uiNodesMap],
    );

    useEffect(() => {
        setLayoutModules(mappedFloorModules);
    }, [mappedFloorModules, setLayoutModules]);

    /**
     * Sync initial floor graph lines to grid area editor
     */
    const graphLines = useAtomValue(layoutFloorGraphLinesAtom);

    const setGraphLines = useSetAtom(graphLinesMapAtom);

    const graphLinesMap = useMemo(() => {
        return graphLines.reduce(
            (acc, customization) => {
                if (!customization.parentUiNodeId) return acc;
                if (!acc[customization.parentUiNodeId]) acc[customization.parentUiNodeId] = [];
                acc[customization.parentUiNodeId].push(customization.childUiNodeId);
                return acc;
            },
            {} as Record<string, string[]>,
        );
    }, [graphLines]);

    useEffect(() => {
        setGraphLines(graphLinesMap);
    }, [graphLinesMap, mappedFloorModules, setGraphLines, setLayoutModules]);
};

const useMapModuleUpdatePayload = () => {
    const activeUserId = useAtomValue(activeUserIdAtom);
    const keyParameter = useAtomValue(layoutKeyParameterAtom);
    const kioskModules = useAtomValue(layoutKioskModulesAtom);

    const layout = useAtomValue(layoutAtom);
    const layoutModules = useAtomValue(gridModulesAtom);

    const floorModuleOrder = useMemo(
        () => layoutModules.sort((a, b) => a.index - b.index).map((module) => module.moduleId),
        [layoutModules],
    );
    const kioskModuleOrder = useMemo(() => kioskModules.map((c) => c.childUiNodeId), [kioskModules]);

    /**
     *  We store grid positions in metadata, which is keyed:
     * {
     *  [moduleId: string]: {
     *    index: `grid start index 0 | 1 | 2 | 3;
     *    colSpan: 1 | 2;
     *    rowSpan: 1 | 2.
     *   }
     * }
     */
    const metadata = useMemo(
        () =>
            Object.fromEntries(
                layoutModules.map((module) => [
                    module.moduleId,
                    {
                        index: module.index,
                        colSpan: module.x,
                        rowSpan: module.y,
                    },
                ]),
            ),
        [layoutModules],
    );

    const updatePayload = useMemo(() => {
        const layoutId = layout?.id;
        const layoutOwner = layout?.ownerUserId;
        const layoutName = layout?.name;
        const keyParameterId = keyParameter?.childUiNodeId;

        if (!layoutId || !layoutName || !layoutOwner || !keyParameterId) {
            return null;
        }

        return {
            layoutId,
            keyParameterId,
            name: layoutName,
            userId: activeUserId,
            customizations: {
                kiosk: kioskModuleOrder,
                floor: floorModuleOrder,
            },
            metadata,
        };
    }, [
        activeUserId,
        floorModuleOrder,
        keyParameter?.childUiNodeId,
        kioskModuleOrder,
        layout?.id,
        layout?.name,
        layout?.ownerUserId,
        metadata,
    ]);

    return updatePayload;
};

export const useUpdateFloorLayout = () => {
    const { mutateAsync: updateLayout } = useUpdateLayout();

    const layout = useAtomValue(layoutAtom);

    const graphLinesMap = useAtomValue(graphLinesMapAtom);
    const { mutateAsync: updateParameter } = useUpdateParameters();

    const updatePayload = useMapModuleUpdatePayload();
    const handleUpdateFloor = useCallback(async () => {
        if (!updatePayload) {
            throw new Error('Missing details in update payload');
        }

        await updateLayout(updatePayload);
        await Promise.all(
            Object.entries(graphLinesMap)
                .filter(([, ids]) => !!ids)
                .map(
                    async ([moduleId, parameterIds]) =>
                        await updateParameter({
                            moduleId,
                            parameterIds: parameterIds!,
                            showChildOn: 'floor',
                            layoutId: layout!.id,
                        }),
                ),
        );
    }, [graphLinesMap, layout, updateLayout, updateParameter, updatePayload]);

    return { handleUpdateFloor };
};
